import React, { useEffect, useRef } from 'react';
import { ShieldCheck, Clock, Award, CreditCard, Truck, Leaf, ArrowRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useNavigate } from 'react-router-dom';
import './Features.css';

// Import high-end perfume assets
import imgQuality from '../../assets/2bc9bf8d-eb81-4664-b1ff-0f286998c3f2.jpg';
import imgLongLasting from '../../assets/04d1b47f7a9aa37e71ab857d6e511ec8.jpg';
import imgLuxury from '../../assets/about-hero.jpg';
import imgSecure from '../../assets/e119f159-e1f5-4ec6-9008-380a66731113.jpg';
import imgDelivery from '../../assets/1d511ceb-a632-4d1d-8887-2b8c7a6994c3.jpg';
import imgEco from '../../assets/f5565a7eae2b771734cf6407f5eb309c.jpg';

gsap.registerPlugin(ScrollTrigger);

const features = [
  { 
    num: '01', 
    badge: 'CERTIFIED', 
    icon: <ShieldCheck strokeWidth={1} size={28} />, 
    title: 'Premium Quality', 
    desc: 'We source only the finest, rare ingredients from across the globe to ensure an unmatched olfactory journey.',
    img: imgQuality
  },
  { 
    num: '02', 
    badge: 'HIGH-WEAR', 
    icon: <Clock strokeWidth={1} size={28} />, 
    title: 'Long Lasting', 
    desc: 'Engineered for endurance, our scents are designed to linger for over 24 hours on skin and fabric.',
    img: imgLongLasting
  },
  { 
    num: '03', 
    badge: 'LUXURY', 
    icon: <Award strokeWidth={1} size={28} />, 
    title: 'Luxury Experience', 
    desc: 'From the weight of the bottle to the mist of the spray, every detail is crafted for pure elegance.',
    img: imgLuxury
  },
  { 
    num: '04', 
    badge: 'SECURE', 
    icon: <CreditCard strokeWidth={1} size={28} />, 
    title: 'Secure Payment', 
    desc: 'Your transactions are protected by industry-leading encryption for a worry-free shopping experience.',
    img: imgSecure
  },
  { 
    num: '05', 
    badge: 'EXPRESS', 
    icon: <Truck strokeWidth={1} size={28} />, 
    title: 'Fast Delivery', 
    desc: 'Our global logistics network ensures your signature scent arrives in pristine condition, swiftly.',
    img: imgDelivery
  },
  { 
    num: '06', 
    badge: 'PURE', 
    icon: <Leaf strokeWidth={1} size={28} />, 
    title: 'Eco Friendly', 
    desc: 'Committed to sustainability, our packaging is 100% recyclable without compromising on beauty.',
    img: imgEco
  },
];

const Features = () => {
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const ctx = gsap.context((self) => {
      const wrappers = self.selector('.fcard-pin-wrapper');
      
      gsap.from(wrappers, { 
        y: 80,
        opacity: 0,
        duration: 1.5, 
        stagger: 0.15, 
        ease: "power4.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 90%",
          toggleActions: "play none none none"
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="features-section-grid" ref={sectionRef}>
      <div className="features-grid-header">
        <h2 className="features-title">
          <span className="why-text">WHY</span>
          <span className="escentrum-text">ESCENTRUM</span>
        </h2>
        <div className="features-title-line"></div>
        <p className="features-subtitle">Luxury Standards • Global Presence</p>
      </div>

      <div className="features-grid-container">
        {features.map((f, i) => (
          <div key={i} className="fcard-premium-wrapper">
            <div className="fcard-premium">
              {/* FRONT FACE: Minimalist Luxury with Premium Asset Background */}
              <div className="fcard-face fcard-front">
                <div className="fcard-front-bg">
                  <img src={f.img} alt={f.title} className="fcard-front-img" />
                  <div className="fcard-front-overlay"></div>
                </div>
                
                <div className="fcard-front-content">
                  <div className="fcard-header-lux">
                    <span className="fcard-num-lux">{f.num}</span>
                    <div className="fcard-icon-lux">{f.icon}</div>
                  </div>
                  <div className="fcard-body-lux">
                    <span className="fcard-badge-lux">{f.badge}</span>
                    <h3 className="fcard-title-lux">{f.title}</h3>
                  </div>
                  <div className="fcard-footer-lux">
                    <span className="lux-hint">DISCOVER DETAIL <ArrowRight size={14} /></span>
                  </div>
                </div>
              </div>

              {/* BACK FACE: Professional Storytelling */}
              <div className="fcard-face fcard-back">
                <div className="back-inner">
                  <div className="back-icon-pill">{f.icon}</div>
                  <h3 className="back-title-lux">{f.title}</h3>
                  <div className="back-divider"></div>
                  <p className="back-desc-lux">{f.desc}</p>
                  <button className="back-btn-lux" onClick={() => navigate('/about')}>EXPLORE ARTISANSHIP</button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Features;
